import {
  createContext,
  DependencyList,
  ReactNode,
  useContext,
  useEffect,
} from "react";
import ReactTooltip from "react-tooltip";

/** Hover delay before icon badge tooltips appear. */
export const ICON_BADGE_TOOLTIP_DELAY_MS = 100;

export const CHECK_TYPES_ICON_BADGE_TOOLTIP_ID = "check-types-icon-badge-tooltip";
export const COMPANY_ASSIGN_CHECK_TYPE_ICON_BADGE_TOOLTIP_ID =
  "company-assign-check-type-icon-badge-tooltip";

const IconBadgeTooltipContext = createContext<string | null>(null);

type IconBadgeTooltipProviderProps = {
  tooltipId: string;
  children: ReactNode;
};

/** Supplies the scoped tooltip id for badges rendered in this subtree. */
export function IconBadgeTooltipProvider({
  tooltipId,
  children,
}: IconBadgeTooltipProviderProps) {
  return (
    <IconBadgeTooltipContext.Provider value={tooltipId}>
      {children}
    </IconBadgeTooltipContext.Provider>
  );
}

function useIconBadgeTooltipId(): string {
  const tooltipId = useContext(IconBadgeTooltipContext);
  if (!tooltipId) {
    throw new Error(
      "IconBadgeTooltip must be used within an IconBadgeTooltipProvider"
    );
  }
  return tooltipId;
}

type IconBadgeTooltipProps = {
  label: string;
  children: ReactNode;
  className?: string;
};

/** Marks a badge with tooltip text; pair with a scoped `IconBadgeTooltipLayer`. */
export function IconBadgeTooltip({
  label,
  children,
  className,
}: IconBadgeTooltipProps) {
  const tooltipId = useIconBadgeTooltipId();

  return (
    <span data-tip={label} data-for={tooltipId} className={className}>
      {children}
    </span>
  );
}

type IconBadgeTooltipLayerProps = {
  id: string;
};

/**
 * One shared react-tooltip instance for icon badges in a scope.
 * Mount outside `overflow-x-auto` containers so popovers are not clipped.
 */
export function IconBadgeTooltipLayer({ id }: IconBadgeTooltipLayerProps) {
  return (
    <ReactTooltip
      id={id}
      effect="solid"
      delayShow={ICON_BADGE_TOOLTIP_DELAY_MS}
      delayHide={0}
      className="z-[10000]"
    />
  );
}

/** Re-scan scoped `data-tip` targets after async table rows or filters change. */
export function useRebuildIconBadgeTooltips(deps: DependencyList) {
  useEffect(() => {
    ReactTooltip.rebuild();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
